import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useAppSelector } from '../../data/store'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import Loader from '../components/Loader'
import {
  FormatCurrencyNoDecimals,
  FormatCurrencyToNearestLowerMultipleToNoDecimals,
  FormatCurrencyRoundedUpToNoDecimals,
  FormatCurrencyToNearestUpperMultipleToNoDecimals,
} from '../utils/Formatter'
import {
  EditCreditApplicationSinAndLoanAmount,
  EditCreditApplicationSinAndLoanAmountSchema,
} from '../../data/schemas/PrequalificationSchema'
import { reportErrorToServer } from '../../error-logger'
import { calculateMonthlyAmount } from '../utils/utils'
import { Constants } from '../../data/types/constants'
import { creditApplicationStateSelectors } from '../../data/creditApplicationStateSelectors'
import { trackingEvents } from '../utils/trackingEvents'

type Props = {
  onSubmit: () => void
  cancelMyApp: (event: { preventDefault: () => void }) => void
  gotoAddCoapplicant: () => void
  stepProgression: React.ReactElement
}

function PrequalificationApprovedForm({ onSubmit, cancelMyApp, gotoAddCoapplicant, stepProgression }: Props) {
  const { t } = useTranslation()
  const { id } = useParams()
  const dispatchEffect = useSideEffect()
  const filteredCreditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const minPaymentForRequestedAmount =
    creditApplicationStateSelectors.getMinPaymentForRequestedAmount(filteredCreditApplication)
  const maxPaymentForRequestedAmount =
    creditApplicationStateSelectors.getMaxPaymentForRequestedAmount(filteredCreditApplication)

  const [isSubmitted, setSubmitted] = React.useState<boolean>(false)
  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    reset,
    setValue,
  } = useForm<EditCreditApplicationSinAndLoanAmount>({
    mode: 'onBlur',
    defaultValues: {
      ...EditCreditApplicationSinAndLoanAmountSchema.getDefault(),
    },
    resolver: yupResolver(EditCreditApplicationSinAndLoanAmountSchema),
  })

  const loanAmount = watch('requestedLoanAmount')
  const ApplicantSin = watch('applicantSin')
  const CoApplicantSin = watch('coapplicantSin')
  const [updatedMonthlyPayment, setUpdatedMonthlyPayment] = React.useState<number[]>([0, 0])
  const [toggleLearnMore, setToggleLearnMore] = React.useState<boolean>(false)
  const computedInfo = useAppSelector(creditApplicationSelectors.getComputedInfo)

  const mustAskApplicantSIN = () => {
    return (
      !filteredCreditApplication?.hasApplicantSin &&
      filteredCreditApplication?.prequalificationDecision.applicantCreditTwoYearsLess
    )
  }

  const mustAskCoapplicantSIN = () => {
    return (
      !filteredCreditApplication?.hasCoapplicantSin &&
      filteredCreditApplication?.prequalificationDecision.coapplicantCreditTwoYearsLess
    )
  }

  React.useEffect(() => {
    if (filteredCreditApplication) {
      const computedLoanAmount = Math.min(
        filteredCreditApplication.requestedLoanAmount,
        filteredCreditApplication?.prequalificationDecision?.maxLoanAmount,
      )
      reset({
        ...EditCreditApplicationSinAndLoanAmountSchema.getDefault(),
        needsApplicantSin: mustAskApplicantSIN(),
        needsCoapplicantSin: mustAskCoapplicantSIN(),
        applicantSin: ApplicantSin,
        coapplicantSin: CoApplicantSin,
        requestedLoanAmount: computedLoanAmount ?? 0,
        id: id as string,
      })
    }
  }, [filteredCreditApplication])

  const innerSubmit = (data: EditCreditApplicationSinAndLoanAmount) => {
    setSubmitted(true)
    dispatchEffect(creditApplicationEffects.putUpdateCreditApplication(data)).then(onSubmit).catch(reportErrorToServer)
  }

  React.useEffect(() => {
    if (loanAmount > 0 && filteredCreditApplication?.prequalificationDecision) {
      const minPayment = calculateMonthlyAmount(
        filteredCreditApplication?.applicantAge >= Constants.UpperThresholdAgeForApplicant
          ? Constants.CorrectedMaxTerm
          : Constants.BaseMaxTerm,
        filteredCreditApplication?.prequalificationDecision.minInterestRate,
        loanAmount,
        Constants.FinanceFeeRateForRegular, // a changer pour deferred
      )
      const maxPayment = calculateMonthlyAmount(
        filteredCreditApplication?.applicantAge >= Constants.UpperThresholdAgeForApplicant
          ? Constants.CorrectedMaxTerm
          : Constants.BaseMaxTerm,
        filteredCreditApplication?.prequalificationDecision.maxInterestRate > 0
          ? filteredCreditApplication?.prequalificationDecision.maxInterestRate
          : 29.99,
        loanAmount,
        Constants.FinanceFeeRateForRegular, // a changer pour deferred
      )
      setUpdatedMonthlyPayment([minPayment, maxPayment])
    }
  }, [filteredCreditApplication, loanAmount])

  const mustShowUpadatedMonthlyPayment = React.useMemo(() => {
    return (
      Math.abs(maxPaymentForRequestedAmount - updatedMonthlyPayment[1]) > 5 ||
      Math.abs(minPaymentForRequestedAmount - updatedMonthlyPayment[0]) > 5
    )
  }, [maxPaymentForRequestedAmount, minPaymentForRequestedAmount, updatedMonthlyPayment])

  const hasPromotion = filteredCreditApplication?.merchantPaymentPlanId
  React.useEffect(() => {
    if (hasPromotion) {
      dispatchEffect(
        creditApplicationEffects.computePersonalLoanFunding({ id: filteredCreditApplication?.id as string }),
      )
    }
  }, [])

  const prequalificationAmount = Math.min(
    filteredCreditApplication?.requestedLoanAmount ?? Number.MAX_VALUE,
    filteredCreditApplication?.prequalificationDecision?.maxLoanAmount ?? Number.MAX_VALUE,
  )

  React.useEffect(() => {
    if (filteredCreditApplication && prequalificationAmount) {
      trackingEvents.prequalSuccess({
        credit_application_id: filteredCreditApplication.id,
        reference_number: filteredCreditApplication.referenceNumber,
        amount: prequalificationAmount,
      })
    }
  }, [filteredCreditApplication, prequalificationAmount])

  const prequalifiedMessage =
    prequalificationAmount >= (filteredCreditApplication?.requestedLoanAmount ?? Number.MAX_VALUE)
      ? 'prequalification.prequalified'
      : 'prequalification.prequalifiedNotRequestedAmount'

  return (
    <>
      {isSubmitted && (
        <Loader
          title={t('finalDecision.processing')}
          description={`${t('common.pleaseWait')} ${t('common.withYouShortly')}`}
        />
      )}

      {!isSubmitted && (
        <main className="general-message" style={{ paddingTop: '15rem' }}>
          {stepProgression}
          <form onSubmit={handleSubmit(innerSubmit)}>
            <h1>
              {!filteredCreditApplication?.merchantPaymentPlanId && (
                <Trans
                  i18nKey={prequalifiedMessage}
                  values={{
                    requestedAmount: FormatCurrencyRoundedUpToNoDecimals(prequalificationAmount).replace(' ', '\u202F'),
                    minMonthlyPayment: FormatCurrencyToNearestLowerMultipleToNoDecimals(
                      minPaymentForRequestedAmount,
                      5,
                    ).replace(' ', '\u202F'),
                    maxMonthlyPayment: FormatCurrencyToNearestUpperMultipleToNoDecimals(
                      maxPaymentForRequestedAmount,
                      5,
                    ).replace(' ', '\u202F'),
                  }}
                />
              )}
              {filteredCreditApplication?.merchantPaymentPlanId && computedInfo?.paymentForFrequency && (
                <Trans
                  i18nKey="prequalification.prequalifiedVariableInterest"
                  values={{
                    requestedAmount: FormatCurrencyRoundedUpToNoDecimals(prequalificationAmount).replace(' ', '\u202F'),
                    monthlyPayment: FormatCurrencyToNearestUpperMultipleToNoDecimals(
                      computedInfo.paymentForFrequency,
                      5,
                    ).replace(' ', '\u202F'),
                  }}
                />
              )}
            </h1>
            <div className="paragraph">
              <p className="information">
                <Trans i18nKey="prequalification.loanDetails.mainDetails" />
                <br />
                <span // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setToggleLearnMore(!toggleLearnMore)}
                >
                  <i
                    className={toggleLearnMore ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {t('prequalification.loanDetails.learnMore')}
                </span>
              </p>
              {toggleLearnMore && (
                <p
                  style={{
                    fontStyle: 'italic',
                    border: '1px solid #08abf0',
                    borderRadius: '1.5rem',
                    textAlign: 'center',
                    padding: '1.5rem',
                  }}
                >
                  {t('prequalification.loanDetails.learnMoreDetails')}
                </p>
              )}
              <p>
                <Trans
                  i18nKey="prequalification.prequalificationMaxAmount"
                  values={{
                    maxLoanAmount: FormatCurrencyNoDecimals(
                      filteredCreditApplication?.prequalificationDecision?.maxLoanAmount,
                    ).replace(' ', '\u202F'),
                  }}
                />
              </p>
              <div className={`control-group ${errors.requestedLoanAmount && 'error'}`}>
                <label htmlFor="requestedLoanAmount">{t('prequalification.confirmLoanAmount')}</label>
                <div className="number-wrap">
                  <input
                    id="requestedLoanAmount"
                    defaultValue={loanAmount}
                    type="number"
                    {...register('requestedLoanAmount')}
                    max={filteredCreditApplication?.prequalificationDecision?.maxLoanAmount}
                    min={500}
                    onChange={(e) => {
                      setValue('requestedLoanAmount', e.target.valueAsNumber)
                      if (
                        filteredCreditApplication?.prequalificationDecision != null &&
                        (e.target.valueAsNumber > filteredCreditApplication?.prequalificationDecision?.maxLoanAmount ||
                          e.target.valueAsNumber < 500)
                      ) {
                        e.currentTarget.setCustomValidity(
                          `${t('prequalification.requestedAmountError', {
                            maxLoanAmount:
                              filteredCreditApplication?.prequalificationDecision?.maxLoanAmount.toString(),
                          })}`,
                        )
                      } else {
                        e.currentTarget.setCustomValidity('')
                      }
                    }}
                  />
                </div>
                <div>
                  {/* eslint-disable indent */}
                  {!filteredCreditApplication?.merchantPaymentPlanId && mustShowUpadatedMonthlyPayment && (
                    <p style={{ fontStyle: 'italic' }}>
                      <Trans
                        i18nKey="prequalification.modifiedLoanMessage"
                        values={{
                          minMonthlyPayment: FormatCurrencyToNearestLowerMultipleToNoDecimals(
                            updatedMonthlyPayment[0],
                            5,
                          ),
                          maxMonthlyPayment: FormatCurrencyToNearestUpperMultipleToNoDecimals(
                            updatedMonthlyPayment[1],
                            5,
                          ),
                        }}
                      />
                    </p>
                  )}
                </div>
              </div>

              {mustAskApplicantSIN() && (
                <div className={`control-group  ${errors.applicantSin && 'error'}`}>
                  <p>{t('prequalification.applicantCreditLessThan2Years')}</p>
                  <label htmlFor="insurance-number">{t('personalInformation.sin')}</label>
                  <input
                    id="insurance-number"
                    placeholder="XXX-XXX-XXX"
                    className="sin-input"
                    type="text"
                    {...register('applicantSin')}
                  />
                </div>
              )}

              {mustAskCoapplicantSIN() && (
                <div className={`control-group  ${errors.coapplicantSin && 'error'}`}>
                  <p>{t('prequalification.coapplicantCreditLessThan2Years')}</p>
                  <label htmlFor="insurance-number-co">{t('personalInformation.sin')} </label>
                  <input
                    id="insurance-number-co"
                    placeholder="XXX-XXX-XXX"
                    className="sin-input"
                    type="text"
                    {...register('coapplicantSin')}
                  />
                </div>
              )}

              <p style={{ fontWeight: 'bold' }}>{t('prequalification.pullCreditReport')}</p>
              <div className="control-group">
                <input
                  required
                  type="checkbox"
                  id="acceptConditions"
                  onChange={(e) => {
                    if (e.currentTarget.checked === true) e.currentTarget.setCustomValidity('')
                  }}
                  onInvalid={(e) => e.currentTarget.setCustomValidity(t<string>('common.acceptConditions'))}
                />
                <label style={{ fontSize: '1.8rem', fontWeight: 'inherit' }} title="test" htmlFor="acceptConditions">
                  {t('prequalification.acceptConditions')}
                </label>
              </div>
            </div>
            <div className="button-group-3">
              <div className="item">
                <button type="button" className="btn" onClick={cancelMyApp}>
                  <span>{t<string>('common.cancelMyApp')}</span>
                  <i className="fa-regular fa-ban" />
                </button>
              </div>
              <div className="item">
                <button
                  type="button"
                  className="btn"
                  onClick={gotoAddCoapplicant}
                  disabled={filteredCreditApplication?.hasCoapplicant}
                >
                  <span>{t<string>('common.addCoapplicant')}</span>
                  <i className="fa-regular fa-user-plus" />
                </button>
              </div>
              <div className="item">
                <button type="submit" className="btn btn-blue" disabled={isSubmitted}>
                  <span>{t('prequalification.continue')}</span>
                  <i className="fa-regular fa-arrow-right" />
                </button>
              </div>
            </div>
          </form>
          <div style={{ borderTop: '1px solid black', marginTop: '5rem' }}>
            <p style={{ marginTop: '2rem' }}>
              {!filteredCreditApplication?.merchantPaymentPlanId && (
                <Trans
                  i18nKey="prequalification.priorToQualifyNotes.1"
                  values={{
                    minInterestRate: filteredCreditApplication?.prequalificationDecision.minInterestRate,
                    ageConditionalTerm:
                      filteredCreditApplication?.applicantAge &&
                      filteredCreditApplication?.applicantAge >= Constants.UpperThresholdAgeForApplicant
                        ? Constants.CorrectedMaxTerm
                        : Constants.BaseMaxTerm,
                  }}
                />
              )}
              {filteredCreditApplication?.merchantPaymentPlanId && computedInfo?.paymentForFrequency && (
                <Trans
                  i18nKey="prequalification.priorToQualifyNotes.ForVariableInterest"
                  values={{
                    reducedInterestRate: filteredCreditApplication?.merchantPaymentPlan.reducedInterestRate,
                    reducedInterestRateDurationInMonth:
                      filteredCreditApplication?.merchantPaymentPlan.reducedRateDurationInMonths,
                    interestRate: filteredCreditApplication?.merchantPaymentPlan.interestRate,
                    borrowerFee: filteredCreditApplication?.merchantPaymentPlan.borrowerFeeRate,
                  }}
                />
              )}
            </p>
            <p style={{ marginTop: '2rem' }}>{t('prequalification.priorToQualifyNotes.2')}</p>
          </div>
        </main>
      )}
    </>
  )
}

export default PrequalificationApprovedForm
